import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Slider,
  Typography,
  Box,
  Snackbar,
  Alert,
  //CircularProgress,
  Paper,
  IconButton,
  Collapse,
  Button,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  //Divider,
  Container
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { debounce } from 'lodash';
import IconWithProgress from './IconWithProgress';
// const modelFormConfig = useSelector(
//   state => state.config.configs.evaluationFormConfig?.fields || []
// );
// Removed evaluationCriteria import - now using state.configs.riskEvaluationForm
import {
  fetchRiskEvaluations,
  updateRiskEvaluation,
  setRiskPriority,
  updateRiskPriority,
} from './redux/actions/riskActions.js';

// Helper to convert strings
const toTitleCase = (str = '') =>
  str
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.length > 0
      ? word[0].toUpperCase() + word.slice(1).toLowerCase()
      : ''
    )
    .join(' ');

const sliderColorScale = [
  '#BDBDBD',
  '#4CAF50',
  '#FFA726',
  '#FFC107',
  '#FF1744',
  '#000000',
];

const steps = ['Evaluation', 'Priority & Rationale'];

const RiskEvaluationForm = ({
  modelId,
  riskName,
  riskId,
  assessmentUuid,
  riskArea,
  handleSaveOperation,
  category,
  onCloseDrawer,
  hideTitle = false
}) => {
  const themes = useSelector((state) => state.themes);
  const evaluationCriteria = useSelector((state) => state.config?.configs?.riskEvaluationForm || []);
  const dispatch = useDispatch();
 
  const savedRisk = useSelector((state) => {
    const risks =
      state.risks.models[modelId]?.riskFormData[riskArea]?.sectionRisks?.[category] || [];
    return risks.find((r) => r.riskId === riskId);
  });
  const riskLoading = savedRisk?.loading || false;
  // Basic UI States
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [deepDiveOpen, setDeepDiveOpen] = useState({});

  // Priority & Rationale states
  const [localPriority, setLocalPriority] = useState('Select');
  const [localRationale, setLocalRationale] = useState('');

  // Stepper
  const [activeStep, setActiveStep] = useState(0);

  // Confirm dialog if user tries to close without setting priority
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // Fetch existing evaluations on mount
  useEffect(() => {
    const fetchData = async () => {
          await dispatch(fetchRiskEvaluations(modelId, assessmentUuid, riskArea, category));
       };
    fetchData();
  }, [dispatch, modelId, assessmentUuid, riskArea, category]);

  // Initialize local states if risk has priority/rationale
  useEffect(() => {
    if (savedRisk?.priority) setLocalPriority(savedRisk.priority);
    if (savedRisk?.rationale) setLocalRationale(savedRisk.rationale);
  }, [savedRisk]);

  // Debounced saving for slider-based evaluation
  const debouncedSaveEvaluation = useMemo(
    () =>
      debounce(async (themeId, attribute, property, value, priority, rationale) => {
        if (!savedRisk?.evaluationData) return;
        try {
          const response = await fetch('/.netlify/functions/saveRiskEvaluation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              modelUuid: modelId,
              assessmentUuid,
              riskName,
              riskId,
              themeId,
              category,
              priority,
              rationale,
              data: {
                [property]: value,
              },
            }),
          });
          if (!response.ok) {
            throw new Error('Failed to save evaluation');
          }
        } catch (error) {
          setSnackbarMessage(`Failed to save risk evaluation: ${error.message}`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }, 400),
    [modelId, assessmentUuid, riskName, riskId, savedRisk, category]
  );

  // Update Redux + auto-save for sliders
  const handleSliderChange =
    (themeId, attribute, property, options) => (e, sliderValue) => {
      const priority = savedRisk?.priority || '';
      const rationale = savedRisk?.rationale || '';
      const value = options[sliderValue];

      if (savedRisk) {
        dispatch(
          updateRiskEvaluation(
            modelId,
            riskId,
            riskArea,
            themeId,
            attribute,
            property,
            value,
            priority,
            rationale,
            category
          )
        );
        handleSaveOperation(() =>
          debouncedSaveEvaluation(themeId, attribute, property, value, priority, rationale)
        );
      }
    };

  // Toggle deep-dive collapsible
  const toggleDeepDive = (themeId, attribute) => {
    setDeepDiveOpen((prev) => ({
      ...prev,
      [`${themeId}-${attribute}`]: !prev[`${themeId}-${attribute}`],
    }));
  };


  if (!savedRisk) {
    return <Typography>Risk not found.</Typography>;
  }

  // Ensure we have an evaluationData object
  if (!savedRisk.evaluationData) {
    savedRisk.evaluationData = {};
  }

  // Step 1: The slider-based evaluation
  const renderEvaluationStep = () => (
    <Container maxWidth="md" sx={{ px: 0 }}>
      <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {themes.map((theme) => (
          <Paper
            key={theme.theme_id}
            elevation={1}
            sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              {theme.name}
            </Typography>
            {evaluationCriteria.map((criterion) => (
              <Box key={criterion.attribute} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {toTitleCase(criterion.attribute)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => toggleDeepDive(theme.theme_id, criterion.attribute)}
                    aria-label="Deep Dive"
                  >
                    {deepDiveOpen[`${theme.theme_id}-${criterion.attribute}`] ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </IconButton>
                </Box>

                {/* Base slider - improved container to prevent overflow */}
                <Box sx={{ display: 'flex', mb: 2, width: '90%' }}>
                 
                  <Box sx={{ width: '100%', pl: 2, pr: 1 }}>
                    <Slider
                      aria-label={`Slider for ${toTitleCase(criterion.baseEvaluation.name)}`}
                      value={criterion.baseEvaluation.options.indexOf(
                        savedRisk.evaluationData[theme.theme_id]?.[criterion.attribute]?.[
                          criterion.baseEvaluation.name
                        ] || ''
                      )}
                      step={1}
                      min={0}
                      max={criterion.baseEvaluation.options.length - 1}
                      marks={criterion.baseEvaluation.options.map((o, i) => ({
                        value: i,
                        label: o,
                      }))}
                      onChangeCommitted={handleSliderChange(
                        theme.theme_id,
                        criterion.attribute,
                        criterion.baseEvaluation.name,
                        criterion.baseEvaluation.options
                      )}
                      valueLabelDisplay="off"
                      sx={{
                        color: sliderColorScale[
                          Math.min(
                            criterion.baseEvaluation.options.indexOf(
                              savedRisk.evaluationData[theme.theme_id]?.[criterion.attribute]?.[
                                criterion.baseEvaluation.name
                              ] || ''
                            ),
                            sliderColorScale.length - 1
                          )
                        ],
                        '& .MuiSlider-markLabel': {
                          fontSize: '0.75rem',
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Deep dive collapse */}
                <Collapse
                  in={deepDiveOpen[`${theme.theme_id}-${criterion.attribute}`]}
                  timeout="auto"
                  unmountOnExit
                >
                  <Paper elevation={1} sx={{ p: 2, mt: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{fontWeight: 600,  mb: 2 }}>
                      {toTitleCase(criterion.attribute)} Deep Dive
                    </Typography>
                    {criterion.deepDive.map((field) => {
                      const currentValue =
                        savedRisk.evaluationData[theme.theme_id]?.[criterion.attribute]?.[
                          field.name
                        ] || '';
                      const sliderIndex = field.options.indexOf(currentValue);
                      const valueIndex = sliderIndex >= 0 ? sliderIndex : 0;
                      const colorIndex = Math.min(valueIndex, sliderColorScale.length - 1);
                      const sliderColor = sliderColorScale[colorIndex];

                      return (
                        <Box key={field.name} sx={{ mb: 2, width: '90%', mx: 'auto' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          {toTitleCase(field.name)}
                        </Typography>
                          <Box sx={{ width: '90%', pl: 2, pr: 1 }}>
                            <Slider
                              value={valueIndex}
                              step={1}
                              min={0}
                              max={field.options.length - 1}
                              marks={field.options.map((o, i) => ({ value: i, label: o }))}
                              onChangeCommitted={handleSliderChange(
                                theme.theme_id,
                                criterion.attribute,
                                field.name,
                                field.options
                              )}
                              valueLabelDisplay="off"
                              sx={{ 
                                color: sliderColor,
                                '& .MuiSlider-markLabel': {
                                  fontSize: '0.75rem',
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Paper>
                </Collapse>
              </Box>
            ))}
          </Paper>
        ))}
      </Box>
    </Container>
  );

  // Copy old "Set Priority" logic from the accordion
  const handleSetPriority = async (theRisk) => {
    handleSaveOperation(async () => {
      const themeIds = Object.keys(theRisk.evaluationData || {});
      try {
        await Promise.all(
          themeIds.map(async (themeId) => {
            const numericThemeId = parseInt(themeId, 10);
            const themeName = themes.find((t) => t.theme_id === numericThemeId)?.name;
            const evaluationData = theRisk.evaluationData[themeId]?.impact;

            await dispatch(
              setRiskPriority(
                modelId,
                theRisk.riskId,
                riskArea,
                category,
                theRisk.riskName,
                numericThemeId,
                themeName,
                evaluationData,
                assessmentUuid
              )
            );
          })
        );
      } catch (error) {
        console.error('Error setting priority:', error);
      }
    });
  };

  const renderPriorityStep = () => (
    <Container maxWidth="md" sx={{ px: 0 }}>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          width: '100%',
          maxWidth: 800,
          mx: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Priority & Rationale for {riskName}
        </Typography>
  
        {localPriority === 'Select' ? (
          <Button
            variant="outlined"
            onClick={() => handleSetPriority(savedRisk)}
            color="primary"
            size="large"
            disabled={riskLoading}
            startIcon={<IconWithProgress isLoading={riskLoading} />}
            sx={{ mb: 2 }}
          >
            {riskLoading ? 'Setting...' : 'Set Priority'}
          </Button>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 2 }}>
              Override Priority & Rationale:
            </Typography>
            <Select
              value={localPriority}
              onChange={(e) => setLocalPriority(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ width: '60%', mb: 2 }}
            >
              <MenuItem value="Select" disabled>
                Select
              </MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="significant">Significant</MenuItem>
              <MenuItem value="moderate">Moderate</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="negligible">Negligible</MenuItem>
            </Select>
            <TextField
              label="Rationale"
              multiline
              rows={3}
              value={localRationale}
              onChange={(e) => setLocalRationale(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              disabled={localPriority === 'Select'}
              onClick={handlePrioritySubmit}
            >
              Update Priority & Rationale
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );

  // Actually update localPriority/localRationale
  const handlePrioritySubmit = async () => {
    if (localPriority === 'Select') return;
    await handleSaveOperation(async () => {
      await dispatch(
        updateRiskPriority(
          modelId,
          riskId,
          riskArea,
          category,
          localPriority,
          assessmentUuid,
          localRationale
        )
      );
    });
    setSnackbarMessage('Priority & Rationale updated successfully!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // If user tries to close (or go Back from Step 0) & localPriority is "Select"
  const handleBack = () => {
    if (activeStep === 0 && localPriority === 'Select') {
      setConfirmCloseOpen(true);
    } else if (activeStep === 0 && onCloseDrawer) {
      // No priority or user is fine skipping
      onCloseDrawer();
    } else if (activeStep === 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  // Close the confirm dialog, do nothing
  const handleConfirmCancel = () => {
    setConfirmCloseOpen(false);
  };
  // Close the confirm dialog, user chooses to skip setting priority
  const handleConfirmSkip = () => {
    setConfirmCloseOpen(false);
    if (onCloseDrawer) onCloseDrawer();
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        p: 0,
        overflowX: 'hidden', // no horizontal scroll
      }}
    >
      {/* Improved header with better structure */}
      {!hideTitle && (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: '0.5rem 0.5rem 0 0',
          backgroundColor: '#f5f7fa',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600,
            color: '#2c3e50'
          }} 
          gutterBottom
        >
          Risk Evaluation
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#5a6c7d',
            fontWeight: 500 
          }}
        >
          {riskName}
        </Typography>
      </Paper>
      )}
      <Container maxWidth="md" sx={{ px: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <Stepper activeStep={activeStep} sx={{ width: '80%' }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Container>

      {activeStep === 0 && renderEvaluationStep()}
      {activeStep === 1 && renderPriorityStep()}

      <Container maxWidth="md" sx={{ px: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, mb: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleBack}
            sx={{ px: 4 }}
          >
            {activeStep === 0 ? 'Close' : 'Back'}
          </Button>
          {activeStep === 0 && (
            <Button 
              variant="contained" 
              onClick={handleNext}
              sx={{ px: 4 }}
            >
              Next
            </Button>
          )}
        </Box>
      </Container>

      {/* Confirm dialog if user tries to close without priority */}
      <Dialog open={confirmCloseOpen} onClose={handleConfirmCancel}>
        <DialogTitle>Priority Not Set</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You haven&apos;t set a priority yet. Do you want to skip setting one?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel}>Continue Editing</Button>
          <Button onClick={handleConfirmSkip} color="error">
            Skip Priority
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RiskEvaluationForm;